import { forOwn, fromPairs, last } from 'lib/helpers';
import _cloneDeep from 'lodash/cloneDeep';
import _flatMap from 'lodash/flatMap';
import _mergeWith from 'lodash/mergeWith';

import { parseCELQuery, parseGDPQuery, parseParamValue } from '@rcg/query-parser';
import {
	ANALYTICS,
	BRAND_CEL,
	BRAND_RCI,
	EMAIL_ID,
	FACET,
	GROUP_ID,
	MULTI_QUERY_PARAM_DIVIDER,
	NLS_PARAM,
	QUALIFIERS,
	QUERY_PARAM_ALLOCATOR,
	QUERY_PARAM_DIVIDER,
	SEARCH,
	SORT,
} from '../constants';

/**
 * Formats search object to route query param
 * e.g. { destination: ['BAHAM', 'CARIB'], ship: ['SC'] } => 'search=destination:BAHAM,CARIB&ship:SC'
 * @param {object} search Current search filters selected, being formatted to appply
 * @returns {string} "search=" portion of route search string
 */
export const parseSearchToRouteQuery = (search, queryParamsObject) => {
	if (!search || Object.keys(search).length === 0) {
		return null;
	}

	let formattedQuery = [];
	let searchQuery = [];

	if (!Object.keys(queryParamsObject || {}).length) {
		searchQuery = Object.keys(search).map((key) => {
			const value = search[key];

			if (!value) {
				return null;
			}

			const arrayToString = value.join(',');

			if (!arrayToString) {
				return null;
			}

			return `${key}:${arrayToString}`;
		});
		formattedQuery = searchQuery.filter(Boolean).join('|');
	} else {
		forOwn(queryParamsObject, (value, item) => {
			forOwn(value, (_, key) => {
				if (!searchQuery[key]) {
					searchQuery[key] = [];
				}

				searchQuery[key].push(`${item}:${value[key]}`);
			});
		});

		const compatecSearchQuery = searchQuery.filter(Boolean);
		formattedQuery = Object.values(compatecSearchQuery)
			.map((value) => value?.join(QUERY_PARAM_DIVIDER))
			.join(MULTI_QUERY_PARAM_DIVIDER);
	}
	if (!formattedQuery) return null;
	return `search=${formattedQuery}`;
};

export const parseAnalyticsParamsToRouteQuery = (anatytics) => {
	if (!anatytics || Object.keys(anatytics).length === 0) {
		return null;
	}

	return anatytics;
};

/**
 * Get By and Order params from sort query
 * e.g. by:PRICE|order:ASC => { by: 'PRICE', order: 'ASC' }
 * e.g. by:RECOMMENDED => { by: 'RECOMMENDED' }
 * @param {string} sortQuery Sort query string
 * @returns {object} Object with By and Order params
 */
export const getByAndOrderParams = (sortQuery = '') => {
	const [byStringParam, orderStringParam] = sortQuery.split('|');
	const by = (byStringParam || '').split(':').pop();

	if (!orderStringParam) {
		return { by };
	}

	const order = orderStringParam.split(':').pop();

	return { by, order };
};
/**
 * Formats sort object to route query param
 * e.g. [PRICE,DESC] => 'sort=by:PRICE|order:DESC'
 * e.g. [RECOMMENDED] => 'sort=by:RECOMMENDED'
 * @param {object} sort Sort option selected
 * @returns {string} "sort=" portion of route search string
 */
export const parseSortToRouteQuery = (sort = []) => {
	let [by, order] = sort;
	if (!by && !order) return null;
	if (!order) return `sort=by:${by}`;
	by = 'by:' + by;
	order = 'order:' + order;
	const sortFilter = [by, order].filter(Boolean);
	return `sort=${sortFilter.join('|')}`;
};

/**
 * Formats qualifiers array to route query param
 * e.g. ['seniors', 'miliary'] => 'qualifiers=seniors,miliary'
 * @param {array} qualifiers Array of qualifier strings
 * @returns {string} "qualifiers=" portion of route search string
 */
export const parseQualifiersToRouteQuery = (qualifiers) => {
	if (!qualifiers || qualifiers.length === 0 || !Array.isArray(qualifiers)) {
		return null;
	}

	return `qualifiers=${qualifiers.join(',')}`;
};

/**
 * Formats itineraryPanel url param
 * e.g. 'FR03MIA-2714833697' => 'itineraryPanel=FR03MIA-2714833697'
 * @param {string} groupId The group id for the modal detail
 * @returns {string} "itineraryPanel=" portion of route search string
 */
export const parseItineraryToQuery = (groupId) => {
	if (!groupId) return null;
	return `itineraryPanel=${groupId}`;
};

/**
 * Formats searchSelecting obj to query param
 * e.g. { destination: ['BAHAM', 'CARIB'], ship: ['SC'] } => 'destination:BAHAM,CARIB&ship:SC'
 * @param {object} search Current search filters selected, being formatted to apply
 * @returns {string} filters query for on the fly facet info.
 */
export const parseFacetToQuery = (search) => {
	if (!search || Object.keys(search).length === 0) {
		return null;
	}

	const searchQuery = Object.keys(search)
		.map((key) => {
			const value = search[key];

			if (!value) {
				return null;
			}

			const arrayToString = value.join(',');

			if (!arrayToString) {
				return null;
			}

			return `${key}:${arrayToString}`;
		})
		.filter(Boolean);

	const formattedQuery = searchQuery.join('|');

	if (!formattedQuery) {
		return null;
	}

	return `${formattedQuery}`;
};

/**
 * Formats sort search param as sort variable to be passed to cruise search API
 * e.g. 'PRICE,DESC' => { "by": "PRICE", "order": "DESC" }
 * e.g. 'RECOMMENDED' => { "by": "RECOMMENDED" }
 * @param {string} sort Sort search param
 * @param {string} defaultorder Default sorting option (op)
 * @returns {string} Stringified sort object variable to pass to cruise search API
 */
export const makeSortVariable = (sort = '', defaultOrder) => {
	if (!sort) return defaultOrder || undefined;
	const { by, order } = getByAndOrderParams(sort);
	const variable = { by };
	if (order) {
		variable.order = order;
	}
	return variable;
};

/**
 * @param {string} nlSearch - Natural lenguage search
 * @returns "q=[nlSearch]"
 */
const parseNLToRouteQuery = (nlSearch) => {
	if (!nlSearch) return null;
	return `${NLS_PARAM}=${nlSearch}`
}

const queryParamParsers = {
	[SEARCH]: parseSearchToRouteQuery,
	[SORT]: parseSortToRouteQuery,
	[QUALIFIERS]: parseQualifiersToRouteQuery,
	[FACET]: parseFacetToQuery,
	[GROUP_ID]: parseItineraryToQuery,
	[ANALYTICS]: parseAnalyticsParamsToRouteQuery,
	[NLS_PARAM]: parseNLToRouteQuery,
	// [PAGINATION]
};

/**
 * Creates properly formatted search query string
 * from search filters objects saved in store
 * @param {object} queryParams { search, sort, qualifiers, pagination }
 * @param {array} filterOptions Filter Options received from cruise search API
 * @returns {string}
 */
export const makeRouteQueryFromStore = (queryParams, filterOptions, queryParamsObject = {}) => {
	let routeQuery = '';
	const parsedParams = [];
	Object.entries(queryParams).forEach(([paramName, paramValues]) => {
		const parser = queryParamParsers[paramName];
		if (!parser || !paramValues) return;

		let paramString = parser(paramValues, queryParamsObject[paramName], filterOptions);
		if (!paramString) return;

		if (paramName === 'search') {
			paramString = getParamsSorted(paramString);
		}

		parsedParams.push(paramString);
	});

	if (parsedParams?.length) {
		routeQuery = `?${parsedParams.join('&')}`;
	}

	return routeQuery;
};

/**
 * Transform old query parameters into new query parameters format.
 * @param {string} asPath The path (including the query) shown in the browser.
 * @returns {string} queryString: This is the query params parsed.
 */
export const urlQueryParser = (asPath, brand) => {
	const queryParserMap = {
		[BRAND_CEL.code]: parseCELQuery,
		[BRAND_RCI.code]: parseGDPQuery,
	};

	const queryString = asPath || '';
	const queryFilters = queryParserMap?.[brand]?.(queryString);

	const encodedParams = [EMAIL_ID.urlParam];

	if (queryFilters) {
		const paramFilter = queryFilters.find((element) => element?.key === 'search');
		if (paramFilter) {
			paramFilter.value = getParamsSorted(paramFilter?.value);
		}
	}

	let queryStringParsed = '';
	if (queryFilters) {
		for (const filter of queryFilters) {
			if (filter.key && filter.value) {
				const value = encodedParams.indexOf(filter.key) >= 0 ? encodeURIComponent(filter.value) : filter.value;
				queryStringParsed = queryStringParsed === '' ? `${queryStringParsed}${filter.key}=${value}` : `${queryStringParsed}&${filter.key}=${value}`;
			}
		}
	}

	return queryStringParsed;
};

const compact = (value) => {
	return Array.isArray(value) ? value.filter(Boolean) : [];
};

export const makeParamsObjectFromRouteQuery = (query) => {
	let search = {};
	if (query[SEARCH]) {
		const splitIntoGroupFilters = makeSearchStoreFromRouteQuery(query[SEARCH]);
		let filtersQueries = {};
		splitIntoGroupFilters.forEach((queryGroup, index) => {
			forOwn(queryGroup, (value, key) => {
				if (!key || !value) return null;
				if (!filtersQueries[key]) filtersQueries[key] = {};

				filtersQueries[key][index] = compact(value);
			});
		});
		search = filtersQueries;
	}

	return { search };
};

const makeSearchStoreFromRouteQuery = (query) => {
	const splitIntoGroupFilters = (query || '').split(MULTI_QUERY_PARAM_DIVIDER).map((queryGroup) => {
		const filtersByQuery = queryGroup.split(QUERY_PARAM_DIVIDER);
		const inPairs = filtersByQuery
			.map((filterString) => {
				const [key, value] = filterString.split(QUERY_PARAM_ALLOCATOR);

				if (!key || !value) {
					return null;
				}

				return [key, value.split(',')];
			})
			.filter(Boolean);

		return fromPairs(inPairs);
	});

	return splitIntoGroupFilters;
};

export const addFilterQueryParamsObject = (valueToAdd, filterName, query, queryParamsObject) => {
	const queryGroups = (query || '').split(MULTI_QUERY_PARAM_DIVIDER);
	const cantQueryGroups = queryGroups.length - 1;
	let queryParamsToObject = _cloneDeep(queryParamsObject);
	const filterGroup = queryParamsToObject[SEARCH][filterName];
	const cantQueryGroupsParams = filterGroup ? +last(Object.keys(filterGroup || {})) + 1 : 0;

	if (_flatMap(filterGroup).includes(valueToAdd)) {
		queryParamsToObject = removeFilterQueryParamsObject(valueToAdd, filterName, queryParamsObject);
		return queryParamsToObject;
	}

	if (last(queryGroups).includes(filterName) && filterGroup) {
		queryParamsToObject[SEARCH][filterName] = { ...filterGroup, [cantQueryGroupsParams]: [valueToAdd] };
	} else if (filterGroup) {
		const position = cantQueryGroupsParams > cantQueryGroups ? cantQueryGroupsParams : cantQueryGroups;
		queryParamsToObject[SEARCH][filterName] = { ...filterGroup, [position]: [valueToAdd] };
	} else {
		queryParamsToObject[SEARCH][filterName] = { [cantQueryGroups]: [valueToAdd] };
	}

	return queryParamsToObject;
};

export const removeFilterQueryParamsObject = (valueToRemove, filterName, queryParamsObject) => {
	const queryParamsToObject = { ...queryParamsObject };
	const filterKey = queryParamsToObject[SEARCH]?.[filterName];

	forOwn(filterKey, (value, key) => {
		if (Array.isArray(value)) {
			const filters = value.filter((i) => i != valueToRemove);
			if (!filters.length) delete filterKey[key];
			else filterKey[key] = filters;
			if (!Object.keys(filterKey || {}).length) delete queryParamsToObject[SEARCH][filterName];
			else queryParamsToObject[SEARCH][filterName] = filterKey;
		}
	});

	return queryParamsToObject;
};

export const makeStoreFromRouteQuery = (query /* , filterOptions*/) => {
	let search = {};
	let sort = [];
	let qualifiers = [];
	let analytics = null;
	let q = query[NLS_PARAM] ?? '';

	/**
	 * Search
	 */
	if (query[SEARCH]) {
		const splitIntoGroupFilters = makeSearchStoreFromRouteQuery(query[SEARCH]);

		search = _mergeWith({}, ...splitIntoGroupFilters, (value, srcValue) => {
			if (Array.isArray(value)) return [...new Set(value.concat(srcValue))];
		});
	}

	/**
	 * Sort
	 */
	if (query[SORT]) {
		const { by, order } = getByAndOrderParams(query[SORT]);
		sort = order ? [by, order] : [by];
	}

	/**
	 * Qualifiers
	 */
	if (query[QUALIFIERS]) {
		qualifiers = query[QUALIFIERS].split(',');
	}

	if (query[analytics]) {
		analytics = query[analytics];
	}

	return { search, sort, qualifiers, analytics, q };
};

/**
 * Return code sorted  filters  Alphabetically .
 * @param {object} filters Object with the filters apply.
 * @returns {object} Object: with the sorted  filters apply .
 */
export const getFilterOrdered = (filters) => {
	const { nights, destination, departurePort, ship } = filters;
	return {
		departurePort: departurePort ? departurePort.sort((a, b) => (a > b ? 1 : -1)) : [],
		destination: destination ? destination.sort((a, b) => (a > b ? 1 : -1)) : [],
		nights,
		ship: ship ? ship.sort((a, b) => (a > b ? 1 : -1)) : [],
	};
};

/**
 * Return params soprted  Alphabetically .
 * @param {object} params Object with the params apply.
 * @returns {object} Object: with the sorted  params apply .
 */
export const getParamsSorted = (params) => {
	let prefix = '';
	let _params = params;
	if (typeof params === 'string') {
		if (params.includes('search=')) {
			prefix = 'search=';
			_params = _params.replace(prefix, '');
			_params = _params.replace('?', '');
		}
	}
	const sortParams = _params?.split(MULTI_QUERY_PARAM_DIVIDER).map((queryGroup) => parseParamValue(queryGroup));

	if (!sortParams) return [];

	const optionsToGroup = sortParams.map((group) =>
		Object.keys(group).map((item) => `${item}:${Array.isArray(group[item]) ? group[item].join(',') : group[item]}`),
	);
	const optionsToString = optionsToGroup.map((group) => group.join(QUERY_PARAM_DIVIDER));

	return `${prefix}${optionsToString.join(MULTI_QUERY_PARAM_DIVIDER)}`;
};

export const urlParamsToObject = (urlQuery) => {
	try {
		if (typeof urlQuery === 'string' && urlQuery.length > 0) {
			const search = urlQuery.replace('?', '');
			return JSON.parse('{"' + decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
		}
	} catch {
		return {};
	}
	return {};
};
